// Choose which microphone the wearer hears.
//
// It belongs ON the call panel rather than in a settings page, because the moment an operator
// discovers they are transmitting silence is mid-emergency, and the fix has to be one control away.
// It lives behind the gear (`CallSettingsMenu`) rather than in the controls row: the panel overlays
// the console and every row it costs is a row of map or roster the operator cannot see.
//
// This renders the control only — no chrome, no border — so the menu owns the layout.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { listAudioInputs, readPreferredMic, type AudioInput } from './audio-inputs'

export interface MicPickerProps {
  /** Moves the live call to this device. Rejects if the browser refuses it. */
  onSelect: (deviceId: string) => Promise<void>
}

export function MicPicker({ onSelect }: MicPickerProps) {
  const { t } = useTranslation('console')
  const [devices, setDevices] = useState<AudioInput[]>([])
  const [selected, setSelected] = useState<string>(() => readPreferredMic() ?? '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false

    const refresh = () => {
      void listAudioInputs().then(list => {
        if (!cancelled) setDevices(list)
      })
    }

    refresh()
    // A headset plugged in mid-call is the ordinary way out of this problem, so the list must not be
    // the one captured when the panel opened.
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh)

    return () => {
      cancelled = true
      navigator.mediaDevices?.removeEventListener?.('devicechange', refresh)
    }
  }, [])

  return (
    <label className="flex flex-col gap-1 text-xs text-gray-300">
      <span>{t('alerts.sos.call.micDevice', 'Microphone')}</span>
      <select
        data-testid="sos-call-mic-picker"
        // One device is not a choice, but saying which one it is still answers the question the
        // operator opened this menu with.
        disabled={busy || devices.length < 2}
        value={selected}
        onChange={e => {
          const id = e.target.value

          setSelected(id)
          setBusy(true)
          // Failures are surfaced by the panel's own state — the silence banner stays up if the new
          // device is no better, which is the honest outcome.
          void onSelect(id).finally(() => setBusy(false))
        }}
        className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 disabled:opacity-60"
      >
        {/* Present until the operator chooses, so the control never claims to know which input the
            browser actually picked — `getUserMedia({audio:true})` does not report that back. */}
        {selected === '' && <option value="">{t('alerts.sos.call.micDefault', 'System default')}</option>}
        {devices.map(d => (
          <option key={d.deviceId} value={d.deviceId}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  )
}
